import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const GmailOAuthCallback = () => {
  const [status, setStatus] = useState('processing')
  const navigate = useNavigate()

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get the authorization code from URL
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state') // prefixed state e.g. 'gmail:<tenantId>' or 'calendar:<tenantId>'
        const error = urlParams.get('error')

        if (error) {
          console.error('OAuth error:', error)
          setStatus('error')
          toast.error('Gmail authorization failed: ' + error)
          
          // Close popup and redirect parent
          if (window.opener) {
            window.opener.postMessage({ type: 'GMAIL_OAUTH_ERROR', error }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard/outreach-automation'), 3000)
          }
          return
        }

        if (!code) {
          setStatus('error')
          toast.error('No authorization code received')
          
          if (window.opener) {
            window.opener.postMessage({ type: 'GMAIL_OAUTH_ERROR', error: 'No code' }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard/outreach-automation'), 3000)
          }
          return
        }

        console.log('✅ Authorization code received:', code.substring(0, 20) + '...')
        console.log('🏢 Tenant ID (state):', state)

        // Exchange authorization code for access token
        const CLIENT_ID = '1023666208479-besa8q2moobncp0ih4njtop8a95htop9.apps.googleusercontent.com'
        const CLIENT_SECRET = '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'
        const REDIRECT_URI = window.location.origin + '/oauth/gmail/callback'

        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            code: code,
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            redirect_uri: REDIRECT_URI,
            grant_type: 'authorization_code',
          }),
        })

        const tokenData = await tokenResponse.json()

        if (tokenData.error) {
          console.error('Token exchange error:', tokenData.error)
          setStatus('error')
          toast.error('Token exchange failed: ' + tokenData.error)
          
          if (window.opener) {
            window.opener.postMessage({ type: 'GMAIL_OAUTH_ERROR', error: tokenData.error }, '*')
            window.close()
          } else {
            setTimeout(() => navigate('/dashboard/outreach-automation'), 3000)
          }
          return
        }

        console.log('✅ Tokens received successfully')
        
        // Initialize userEmail variable
        let userEmail = null
        
        // Store tokens in Firebase for the tenant
        if (state && tokenData.access_token) {
          // Get user's email address from the connected Gmail account
          try {
            // Try Gmail profile API first
            const profileResponse = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/profile', {
              headers: {
                'Authorization': `Bearer ${tokenData.access_token}`
              }
            })
            if (profileResponse.ok) {
              const profileData = await profileResponse.json()
              userEmail = profileData.emailAddress
              console.log('✅ Gmail email from profile API:', userEmail)
            } else {
              // Fallback to Google user info API
              console.log('⚠️ Gmail profile API failed, trying user info API...')
              const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                headers: {
                  'Authorization': `Bearer ${tokenData.access_token}`
                }
              })
              if (userInfoResponse.ok) {
                const userInfo = await userInfoResponse.json()
                userEmail = userInfo.email
                console.log('✅ Gmail email from user info API:', userEmail)
              }
            }
          } catch (profileError) {
            console.warn('Could not fetch Gmail profile or user info:', profileError)
            // As a last resort, try to extract email from the OAuth state or other means
            // For now, we'll proceed without email - it can be updated later
          }
          
          // Import Firebase and store the tokens
          // NOTE: Tokens are now saved by the parent component to avoid auth issues in popup
          console.log('📤 Sending tokens to parent window...')
        }

        setStatus('success')
        toast.success('🎉 Gmail connected successfully!')

        // Notify parent window and close popup
        const successType = state?.startsWith('calendar:') ? 'GOOGLE_CALENDAR_OAUTH_SUCCESS' : 'GMAIL_OAUTH_SUCCESS'
        const tenantId = state?.includes(':') ? state.split(':')[1] : state

        if (window.opener) {
          window.opener.postMessage({ 
            type: successType, 
            tenantId,
            tokens: {
              access_token: tokenData.access_token,
              refresh_token: tokenData.refresh_token,
              expires_in: tokenData.expires_in,
              email: userEmail || (successType === 'GMAIL_OAUTH_SUCCESS' ? 'Gmail Connected' : 'Google Calendar Connected')
            }
          }, '*')
          
          setTimeout(() => {
            window.close()
          }, 2000)
        } else {
          setTimeout(() => navigate('/dashboard/outreach-automation'), 3000)
        }

      } catch (error) {
        console.error('OAuth callback error:', error)
        setStatus('error')
        toast.error('OAuth processing failed: ' + error.message)
        
        if (window.opener) {
          window.opener.postMessage({ type: 'GMAIL_OAUTH_ERROR', error: error.message }, '*')
          window.close()
        } else {
          setTimeout(() => navigate('/dashboard/outreach-automation'), 3000)
        }
      }
    }

    handleOAuthCallback()
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="text-6xl mb-4">🔄</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Gmail Authorization</h2>
              <p className="text-gray-600">Please wait while we connect your Gmail account...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Gmail Connected!</h2>
              <p className="text-gray-600">Your Gmail account has been successfully connected for sending emails.</p>
              <p className="text-sm text-gray-500 mt-2">This window will close automatically...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">Connection Failed</h2>
              <p className="text-gray-600">There was an error connecting your Gmail account.</p>
              <p className="text-sm text-gray-500 mt-2">Please try again or check the console for details.</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GmailOAuthCallback